import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, RefreshCw, XCircle, CheckCircle2 } from "lucide-react";

const KIND_LABEL = {
  verified:         "Verified badge",
  listing_featured: "Featured listing",
  brief_featured:   "Clearance request boost",
};

const KIND_HREF = {
  verified:         "/dashboard/verification",
  listing_featured: (t) => (t ? `/listings/${t}` : "/dashboard/listings"),
  brief_featured:   (t) => (t ? `/briefs/${t}` : "/dashboard/briefs"),
};

export default function Subscriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = () =>
    api.get("/subscriptions/mine")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const cancel = async (sub) => {
    if (!window.confirm(`Cancel "${KIND_LABEL[sub.kind] || sub.kind}" auto-renew? It'll remain active until ${new Date(sub.current_period_end).toLocaleDateString("en-GB")}.`)) return;
    setCancelling(sub.stripe_subscription_id);
    try {
      await api.post(`/subscriptions/${sub.stripe_subscription_id}/cancel`);
      toast.success("Auto-renew cancelled — subscription stays active until the period ends.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally { setCancelling(null); }
  };

  if (loading) return <div className="mx-auto max-w-4xl py-16 text-center text-muted-foreground">Loading subscriptions…</div>;

  const active = items.filter((s) => ["active", "trialing", "past_due"].includes(s.status));
  const ended = items.filter((s) => !["active", "trialing", "past_due"].includes(s.status));

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-12">
      <div className="overline text-safety">Billing</div>
      <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-4xl tracking-tighter text-forest">My subscriptions</h1>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-none" data-testid="subs-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      <p className="mt-3 text-muted-foreground">Verified badge, featured listings and brief boosts all live here.</p>

      {items.length === 0 ? (
        <div className="mt-10 border border-border bg-white p-16 text-center" data-testid="subs-empty">
          <CreditCard className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="mt-4 font-display text-xl text-forest">No subscriptions yet</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Upgrade a listing or activate your Verified badge to see subscriptions here.
          </p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <section className="mt-8">
              <div className="overline text-muted-foreground mb-3">Active</div>
              <div className="border border-border bg-white divide-y divide-border">
                {active.map((s) => (
                  <Row key={s.stripe_subscription_id} sub={s} onCancel={cancel} cancelling={cancelling} />
                ))}
              </div>
            </section>
          )}
          {ended.length > 0 && (
            <section className="mt-10">
              <div className="overline text-muted-foreground mb-3">Ended</div>
              <div className="border border-border bg-white divide-y divide-border opacity-70">
                {ended.map((s) => (
                  <Row key={s.stripe_subscription_id} sub={s} readOnly />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Row({ sub, onCancel, cancelling, readOnly }) {
  const label = KIND_LABEL[sub.kind] || sub.kind || "Subscription";
  const targetHref = typeof KIND_HREF[sub.kind] === "function"
    ? KIND_HREF[sub.kind](sub.target_id)
    : KIND_HREF[sub.kind];
  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
  const isCancelling = cancelling === sub.stripe_subscription_id;
  return (
    <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-center" data-testid={`sub-row-${sub.stripe_subscription_id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-display text-lg text-forest">{label}</div>
          <Badge className={`rounded-none overline text-[10px] ${sub.status === "active" ? "bg-forest text-white" : sub.status === "past_due" ? "bg-safety text-white" : "bg-muted text-forest"}`}>
            {sub.status}
          </Badge>
          {sub.cancel_at_period_end && (
            <Badge className="rounded-none bg-safety text-white overline text-[10px]">Auto-renew off</Badge>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground font-mono truncate">{sub.lookup_key || sub.stripe_subscription_id}</div>
        {periodEnd && (
          <div className="mt-1 text-xs text-muted-foreground">
            {sub.cancel_at_period_end ? "Ends" : "Renews"} {periodEnd.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        )}
        {targetHref && (
          <Link to={targetHref} className="mt-1 inline-block text-xs text-safety hover:text-forest overline">Manage →</Link>
        )}
      </div>
      {!readOnly && (
        <div className="justify-self-end">
          {sub.cancel_at_period_end || sub.status === "canceled" ? (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground"><XCircle className="w-4 h-4" /> Won&apos;t renew</span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={isCancelling}
              onClick={() => onCancel(sub)}
              data-testid={`sub-cancel-${sub.stripe_subscription_id}`}
              className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-9 px-4"
            >
              {isCancelling ? "Cancelling…" : "Cancel auto-renew"}
            </Button>
          )}
        </div>
      )}
      {readOnly && (
        <span className="justify-self-end inline-flex items-center gap-1 text-sm text-muted-foreground"><CheckCircle2 className="w-4 h-4" /> Ended</span>
      )}
    </div>
  );
}
